import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { TestTypes } from '@/Models/Configuration';
import { useCreateRecruitmentContext } from '../../../../../../Create/Contexts/CreateRecruitmentContext';
import PersonalityTestCard from './Components/PersonalityTestCard/PersonalityTestCard';
import SkillsTestCard from './Components/SkillsTestCard/SkillsTestCard';
import TestCardGroup from './Components/TestCardGroup/TestCardGroup';
import AptitudeTestCard from './Components/AptitudeTestCard/AptitudeTestCard';
import EmotionalIntelligenceTestCard from './Components/EmotionalIntelligenceTestCard/EmotionalIntelligenceTestCard';
import UIStrings from '@/Utils/UIStrings';
import { DemoQuestions } from '../../../../../../Models/Questions';
import { TestSelectionFormValues } from '../../../../SelectTestsStep';

function TestCards(): React.JSX.Element {
    const { testInfos, getDemoQuestions } = useCreateRecruitmentContext();
    const { testLanguageField } = useWatch<TestSelectionFormValues>();

    const [demoQuestions, setDemoQuestions] = useState<DemoQuestions | undefined>();
    const findTest = useCallback((type: TestTypes) => testInfos.find((item) => item.testType === type), [testInfos]);
    const [personality, skills, aptitude, emotionalIntelligence] = useMemo(
        () => [
            findTest(TestTypes.Personality),
            findTest(TestTypes.Skills),
            findTest(TestTypes.Aptitude),
            findTest(TestTypes.EmotionalIntelligence),
        ],
        [findTest],
    );

    useEffect(() => {
        const getAndSetDemoQuestions = async () => {
            const result = await getDemoQuestions(testLanguageField!);
            setDemoQuestions(result);
        };

        setDemoQuestions(undefined);
        getAndSetDemoQuestions();
    }, [testLanguageField, setDemoQuestions]);

    return (
        <div className="grid justify-center gap-x-4">
            <TestCardGroup groupName={UIStrings.HardSkills}>
                {skills && (
                    <SkillsTestCard
                        test={skills}
                        demoQuestions={demoQuestions?.skills}
                        likertOptions={demoQuestions?.likertAnswers}
                    />
                )}
                {aptitude && (
                    <AptitudeTestCard
                        test={aptitude}
                        demoQuestions={demoQuestions?.aptitude}
                        likertOptions={demoQuestions?.likertAnswers}
                    />
                )}
            </TestCardGroup>
            <TestCardGroup groupName={UIStrings.SoftSkills}>
                {personality && (
                    <PersonalityTestCard
                        test={personality}
                        demoQuestions={demoQuestions?.personality}
                        likertOptions={demoQuestions?.likertAnswers}
                    />
                )}
                {emotionalIntelligence && (
                    <EmotionalIntelligenceTestCard
                        test={emotionalIntelligence}
                        demoQuestions={demoQuestions?.emotionalIntelligence}
                        likertOptions={demoQuestions?.likertAnswers}
                    />
                )}
            </TestCardGroup>
        </div>
    );
}

export default TestCards;
